import {duration, settlementWait} from './MetricsSections';

/**
 * The icons and headline figures each collapsed section shows.
 *
 * Kept out of the tab component so the header summaries sit next to the rule
 * that governs them: a headline is always ONE currency's figure, never a total
 * across currencies, and it says which currency it is.
 */

const Glyph = ({children}) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {children}
  </svg>
);

export const ICONS = {
  flow: (
    <Glyph>
      <path d="M4 20V4" />
      <path d="M8 16v-4M12 16V7M16 16v-6M20 16v-9" />
      <path d="M4 20h17" />
    </Glyph>
  ),
  fees: (
    <Glyph>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.5v9M14.2 9.6a2.4 2.4 0 0 0-4.4 1.2c0 2.4 4.4 1.4 4.4 3.6a2.4 2.4 0 0 1-4.4 1.2" />
    </Glyph>
  ),
  settlement: (
    <Glyph>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.5V12l3 1.8" />
    </Glyph>
  ),
  latency: (
    <Glyph>
      <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />
    </Glyph>
  ),
  quality: (
    <Glyph>
      <path d="M20 6 9 17l-5-5" />
    </Glyph>
  ),
  failures: (
    <Glyph>
      <path d="M12 3.5 21 20H3l9-16.5Z" />
      <path d="M12 10v4M12 17h.01" />
    </Glyph>
  ),
  participation: (
    <Glyph>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.6M17.5 20a5.5 5.5 0 0 0-2.2-4.4" />
    </Glyph>
  ),
};

const number = value =>
  value == null ? '—' : new Intl.NumberFormat().format(value);

/**
 * Money is written out in full, never compacted.
 *
 * "219.1K" hides the difference between 219,095 and 219,149 — on a turnover or
 * a fee figure that is the part someone is reading it for. Long values are
 * handled by stepping the type size down (`sizeClass`), not by rounding.
 */
const full = value =>
  value == null
    ? '—'
    : new Intl.NumberFormat(undefined, {maximumFractionDigits: 2}).format(
        value,
      );

/**
 * A size step for a rendered figure, so a long number shrinks to fit rather
 * than wrapping or overflowing its cell.
 */
export const sizeClass = text => {
  const length = String(text ?? '').length;
  if (length >= 13) return 'Xs';
  if (length >= 10) return 'Sm';
  if (length >= 7) return 'Md';
  return '';
};

/**
 * Picks ONE currency to headline, and says how many others were left out.
 *
 * Never a sum. When a currency is pinned by the filter that one is used;
 * otherwise the largest, which is the one an operator is looking for.
 */
export const headlineMoney = (list, pinnedCurrency) => {
  const entries = Array.isArray(list) ? list : [];
  if (!entries.length) return {value: '0', currency: null, extra: null};

  const chosen = pinnedCurrency
    ? entries.find(entry => entry.currency === pinnedCurrency) ?? entries[0]
    : entries.reduce((best, entry) =>
        Math.abs(entry.amount ?? 0) > Math.abs(best.amount ?? 0) ? entry : best,
      );

  return {
    value: full(chosen.amount),
    currency: chosen.currency,
    extra: entries.length > 1 ? `+${entries.length - 1}` : null,
  };
};

/** The headline figures for each section, given a metrics payload. */
export const sectionHighlights = (metrics, pinnedCurrency) => {
  const flow = metrics?.flow;
  const fees = metrics?.fees;
  const settlement = metrics?.settlement;
  const quality = metrics?.quality;
  const failures = metrics?.failures;
  const participation = metrics?.participation;

  const turnover = headlineMoney(flow?.turnover, pinnedCurrency);
  const charged = headlineMoney(fees?.charged, pinnedCurrency);

  const inFlight = flow?.inFlight?.orderCount ?? 0;
  const oldest = settlement?.oldestUnsettledMs;
  const wait = settlementWait(oldest, inFlight);
  // A week is the point at which an unsettled fill stops being routine — but
  // only when the age is one we can actually vouch for.
  const settlementTone =
    wait.known && oldest > 7 * 24 * 3600 * 1000 ? 'Warn' : null;

  const failureTotal = [
    failures?.rejectedOrderCount,
    failures?.erroredOrderCount,
  ].reduce((sum, value) => sum + (value ?? 0), 0);

  return {
    flow: [
      {label: 'Pending', value: number(flow?.pending?.orderCount)},
      {label: 'Filled', value: number(flow?.everFilled?.orderCount)},
      {
        label: 'Turnover',
        value: turnover.value,
        currency: turnover.currency,
        extra: turnover.extra,
      },
    ],
    fees: [
      {
        label: 'Charged',
        value: charged.value,
        currency: charged.currency,
        extra: charged.extra,
      },
      {
        label: 'Rate',
        value:
          fees?.effectiveRateBps == null
            ? '—'
            : `${fees.effectiveRateBps.toFixed(1)}bps`,
      },
    ],
    settlement: [
      {label: 'Awaiting', value: number(inFlight)},
      {
        label: 'Longest wait',
        value: wait.short,
        tone: settlementTone,
      },
    ],
    latency: [
      {
        label: 'Avg to fill',
        value: duration(metrics?.latency?.timeToFill?.averageMs),
      },
      {
        label: 'Avg to settle',
        value: duration(metrics?.latency?.timeToSettle?.averageMs),
      },
    ],
    quality: [
      {
        label: 'Fill rate',
        value:
          quality?.fillRate == null
            ? '—'
            : `${(quality.fillRate * 100).toFixed(1)}%`,
      },
      {label: 'Under-filled', value: number(quality?.underFilledOrderCount)},
    ],
    failures: [
      {
        label: 'Rejected',
        value: number(failures?.rejectedOrderCount),
        tone: failures?.rejectedOrderCount > 0 ? 'Warn' : null,
      },
      {
        label: 'On hold',
        value: number(failures?.onHoldOrderCount),
        tone: failures?.onHoldOrderCount > 0 ? 'Warn' : null,
      },
    ],
    participation: [
      {label: 'Traders', value: number(participation?.traderCount)},
      {label: 'Instruments', value: number(participation?.instrumentCount)},
    ],
    // Tones for the cards themselves.
    tones: {
      settlement: settlementTone,
      failures: failureTotal > 0 ? 'Warn' : null,
    },
  };
};
